import type { SelectOption, SelectOptionGroup } from 'contracts/Common';
import type { FindSchedulingDocks } from 'contracts/Scheduling';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import { ListSchedulingDocksActions as MainActions } from 'store/ducks/scheduling';
import { Formatter } from 'utils';
import { FindDocksValidator } from 'validators/Scheduling';
import { format } from 'date-fns';

export const useSchedulingDocks = () => {
  const dispatch: AppDispatch = useDispatch();
  const { data: schedulingDocks, loading: loadingSchedulingDocks } =
    useSelector((state: RootState) => state.listSchedulingDocks);

  const [options, setOptions] = useState<SelectOptionGroup<SelectOption>[]>([]);

  const fetchSchedulingDocks = useCallback(
    async (query: Partial<FindSchedulingDocks>): Promise<void> => {
      const { schema, logErrors } = new FindDocksValidator();

      const selectedDateIsBefore =
        query.date && query.date < format(new Date(), 'yyyy-MM-dd');

      if (query.date && selectedDateIsBefore) {
        dispatch(MainActions.success([]));
        return;
      }

      try {
        const validQuery = await schema.validate(query, { abortEarly: false });
        dispatch(MainActions.request(validQuery));
      } catch (error) {
        dispatch(MainActions.reset());
        logErrors(error);
      }
    },
    [dispatch]
  );

  const resetSchedulingDocks = useCallback(() => {
    dispatch(MainActions.success([]));
  }, []);

  const onDocksLoad = useCallback((): void => {
    setOptions(
      schedulingDocks.map(({ dock, timeframes }) => ({
        label: dock.name,
        options: timeframes.map((timeframe) => ({
          label: `${dock.name} às ${Formatter.date(timeframe, {
            format: 'HH:mm',
          })}`,
          value: `${dock.id}|${timeframe}`,
        })),
      }))
    );
  }, [schedulingDocks]);

  useEffect(() => {
    onDocksLoad();
  }, [onDocksLoad]);

  useEffect(() => {
    return () => {
      dispatch(MainActions.reset());
    };
  }, [dispatch]);

  return {
    schedulingDocksOptions: options,
    loadingSchedulingDocks,
    resetSchedulingDocks,
    fetchSchedulingDocks,
  };
};

export type SchedulingDocksHook = ReturnType<typeof useSchedulingDocks>;
